// @flow

/**
 * Owns a conference-info subscription on behalf of a class component.
 *
 * The subscription has three things to get right - starting it, tearing it down,
 * and handling unmount winning the race against the subscribe resolving - and
 * none of them belong in a prejoin screen. Keeping them here means those screens
 * add a field and two lifecycle calls rather than a block of async plumbing.
 *
 * Safe to start() repeatedly: only the first call while running has any effect.
 */
export default class ConferenceInfoController {
    _watch: Function;
    _onUpdate: Function;
    _options: Object;
    _dispose: ?Function;
    _pending: boolean;
    _stopped: boolean;

    /**
     * Initializes a new controller.
     *
     * @param {Function} watch - Bound `watchConferenceInfo` from the component's
     * mapDispatchToProps. Taken as a prop rather than as raw `dispatch` because
     * react-redux does not inject `dispatch` once mapDispatchToProps is supplied,
     * and both screens that use this supply one.
     * @param {Function} onUpdate - Called with each ConferenceInfo received.
     * @param {Object} options - Passed through to `watchConferenceInfo`.
     */
    constructor(watch: Function, onUpdate: Function, options: Object = {}) {
        this._watch = watch;
        this._onUpdate = onUpdate;
        this._options = options;
        this._dispose = null;
        this._pending = false;
        this._stopped = false;
    }

    /**
     * Starts watching a room, if not already watching one.
     *
     * @param {string} roomName - Backend-safe room name.
     * @returns {void}
     */
    start(roomName: string) {
        if (!roomName || !this._watch || this._dispose || this._pending) {
            return;
        }

        this._pending = true;
        this._stopped = false;

        this._watch(roomName, this._onUpdate, this._options)
            .then(dispose => {
                this._pending = false;

                // stop() may have run while the subscription was being set up.
                if (this._stopped) {
                    dispose();

                    return;
                }

                this._dispose = dispose;
            });
    }

    /**
     * Stops watching and releases everything the subscription holds.
     *
     * @returns {void}
     */
    stop() {
        this._stopped = true;
        this._dispose?.();
        this._dispose = null;
    }
}
